import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import html2canvas from 'html2canvas';
import { useAppContext } from 'contexts/AppContext';
import { post } from 'utils/api/api-handler';
import { Icon } from 'utils/icons';
import './styles.scss';

export default function FeedbackWidget({
  userName,
  userEmail,
  buttonLabel = 'Send Feedback',
  toAddress = 'email@gmail.com',
  subject = 'CineBudget Pro User Feedback',
}) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [includeShot, setIncludeShot] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState(null);
  const { userData, userProfile } = useAppContext();

  useEffect(() => {
    if (open) {
      // Reset form state when modal opens
      setFeedback('');
      setError(null);
      setSuccess(false);
      setScreenshotDataUrl(null);
      setIncludeShot(true);
      // Capture screenshot after state is reset
      setTimeout(() => {
        captureScreenshot();
      }, 0);
    }
  }, [open]);

  async function captureScreenshot() {
    try {
      const canvas = await html2canvas(document.body, {
        scale: 1,
        useCORS: true,
        ignoreElements: el =>
          el?.classList?.contains('feedback-modal-overlay') ||
          el?.closest?.('.feedback-modal-overlay'),
      });
      const dataUrl = canvas.toDataURL('image/png');
      setScreenshotDataUrl(dataUrl);
      return dataUrl;
    } catch (e) {
      setError(
        "Couldn't capture the screen. You can still send feedback without a screenshot."
      );
      return null;
    }
  }

  async function handleSend(e) {
    e?.preventDefault?.();
    setLoading(true);
    setError(null);
    try {
      const payload = {
        to: toAddress,
        subject,
        userName: (userData?.name || userProfile?.name) ?? '(unknown)',
        userEmail: userData?.email ?? '(unknown)',
        feedback,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        screenshot: includeShot ? screenshotDataUrl : null,
      };
      await post('/api/feedback/', payload);

      setSuccess(true);
      setFeedback('');
      setScreenshotDataUrl(null);
      setIncludeShot(true);
      setOpen(false);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || 'Failed to send feedback'
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <button
        aria-label={buttonLabel}
        onClick={() => setOpen(true)}
        className="feedback-tab"
        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon
            name="BullhornIcon"
            style={{
              width: '14px',
              height: '14px',
              transform: 'rotate(-180deg)',
            }}
          />
          {buttonLabel}
        </span>
      </button>

      {open && (
        <div className="feedback-modal-overlay">
          <div className="feedback-modal" role="dialog" aria-modal="true">
            <div className="feedback-modal-header">
              <h2>Send Feedback</h2>
              <button
                className="close-button"
                onClick={() => {
                  setOpen(false);
                  // Reset form state when closing
                  setFeedback('');
                  setError(null);
                  setSuccess(false);
                }}
              >
                ✕
              </button>
            </div>

            <form className="feedback-form" onSubmit={handleSend}>
              <div className="user-info-section">
                <div className="user-info-item">
                  <div className="feedback-form-label">Name</div>
                  <div className="feedback-form-display">
                    {(userData?.name || userProfile?.name) ?? 'Unknown'}
                  </div>
                </div>
                <div className="user-info-item">
                  <div className="feedback-form-label">Email</div>
                  <div className="feedback-form-display">
                    {userData?.email ?? '(unknown)'}
                  </div>
                </div>
              </div>

              <div>
                <div className="feedback-form-label">Feedback</div>
              </div>
              <textarea
                value={feedback}
                className="feedback-form-input"
                onChange={e => setFeedback(e.target.value)}
                placeholder="Let us know how can we improve CineBudget Pro"
              />

              <div className="checkbox-row">
                <input
                  type="checkbox"
                  checked={includeShot}
                  onChange={async e => {
                    setIncludeShot(e.target.checked);
                    if (e.target.checked && !screenshotDataUrl)
                      await captureScreenshot();
                  }}
                />
                <div>Include a screenshot of this page</div>
              </div>

              {includeShot && (
                <div className="screenshot-preview">
                  <div className="screenshot-header">
                    <span>Preview</span>
                  </div>
                  {screenshotDataUrl ? (
                    <img src={screenshotDataUrl} alt="Screenshot preview" />
                  ) : (
                    <div className="no-screenshot">
                      No screenshot captured yet.
                    </div>
                  )}
                </div>
              )}

              {error && <div className="error-text">{error}</div>}
              {success && (
                <div className="success-text">
                  Thanks! Your feedback was sent.
                </div>
              )}
            </form>

            <div className="feedback-buttons">
              <button
                className="cancel-button"
                onClick={() => {
                  setOpen(false);
                  // Reset form state when canceling
                  setFeedback('');
                  setError(null);
                  setSuccess(false);
                }}
                type="button"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="send-button"
                onClick={handleSend}
                type="button"
                disabled={loading || !feedback.trim()}
              >
                <span
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Icon
                    name="BullhornIcon"
                    style={{ width: '14px', height: '14px' }}
                  />
                  {loading ? 'Sending…' : 'Send'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

FeedbackWidget.propTypes = {
  userName: PropTypes.string,
  userEmail: PropTypes.string,
  buttonLabel: PropTypes.string,
  toAddress: PropTypes.string,
  subject: PropTypes.string,
};

FeedbackWidget.defaultProps = {
  userName: undefined,
  userEmail: undefined,
  buttonLabel: 'Send Feedback',
  toAddress: 'email@gmail.com',
  subject: 'CineBudget Pro User Feedback',
};
